import React from 'react';
import { Box, Card, CardContent, Typography, useTheme, Skeleton } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';

const StatCard = ({ 
  title, 
  value, 
  subtitle, 
  icon: Icon, 
  trend, 
  trendLabel,
  color = 'primary',
  loading = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  const colorMap = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
  };

  const accentColor = colorMap[color] || colorMap.primary;

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Skeleton variant="rectangular" width={48} height={48} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="70%" height={14} sx={{ mb: 0.5 }} />
              <Skeleton width="50%" height={24} sx={{ mb: 1 }} />
              <Skeleton width="60%" height={14} />
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        borderBottom: `3px solid ${accentColor}`,
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Icon on left - vertically centered */}
          {Icon && (
            <Box
              sx={{
                width: 48,
                height: 48,
                backgroundColor: `${accentColor}15`,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                alignSelf: 'flex-start',
              }}
            >
              <Icon sx={{ color: accentColor, fontSize: 24 }} />
            </Box>
          )}

          {/* Content on right - stacked vertically */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Title */}
            <Typography
              sx={{
                color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)',
                fontFamily: "'Space Mono', monospace",
                fontSize: '0.65rem',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                mb: 0.25,
              }}
            >
              {title}
            </Typography>

            {/* Value */}
            <Typography
              sx={{
                fontFamily: "'DotGothic16', sans-serif",
                fontWeight: 400,
                fontSize: '1.5rem',
                letterSpacing: '0.02em',
                lineHeight: 1.2,
                color: theme.palette.text.primary,
                mb: 0.75,
              }}
            >
              {typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}
            </Typography>

            {/* Trend row - below value, always reserve space */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 18 }}>
              {trend !== undefined ? (
                <>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.35,
                      px: 0.6,
                      py: 0.2,
                      backgroundColor: trend >= 0 
                        ? `${theme.palette.success.main}15` 
                        : `${theme.palette.error.main}15`,
                      borderRadius: 0.75,
                    }}
                  >
                    {trend >= 0 ? (
                      <TrendingUp sx={{ fontSize: 11, color: theme.palette.success.main }} />
                    ) : (
                      <TrendingDown sx={{ fontSize: 11, color: theme.palette.error.main }} />
                    )}
                    <Typography
                      sx={{
                        fontFamily: "'Space Mono', monospace",
                        fontSize: '0.6rem',
                        fontWeight: 500,
                        color: trend >= 0 ? theme.palette.success.main : theme.palette.error.main,
                        lineHeight: 1,
                      }}
                    >
                      {trend >= 0 ? '+' : ''}{trend}%
                    </Typography>
                  </Box>
                  {(subtitle || trendLabel) && (
                    <Typography 
                      sx={{ 
                        color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
                        fontFamily: "'Space Mono', monospace",
                        fontSize: '0.55rem',
                      }}
                    >
                      {subtitle || trendLabel}
                    </Typography>
                  )}
                </>
              ) : (
                /* Placeholder for consistent height */
                <Box sx={{ height: 18 }} />
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default StatCard;
